import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { AppIconComponent } from '../../components/icon/icon.component';
import { Subject, filter, takeUntil } from 'rxjs';
import { InsuranceCategory } from '../../../core/data/dtos/insurance-type.dto';
import { QuotesService } from '../../../core/data/services/quotes.service';
import { getCategoryLabel } from '../../utils/insurance-category.util';

type StepId = 'insurance-types' | 'summary' | 'checkout';

interface StepItem {
  id: StepId;
  label: string;
}

@Component({
  selector: 'app-step',
  imports: [CommonModule, AppIconComponent],
  templateUrl: './step.component.html',
  styleUrl: './step.component.css',
})
export class StepComponent implements OnInit, OnDestroy {
  private readonly quotesService = inject(QuotesService);

  steps: StepItem[] = [
    { id: 'insurance-types', label: 'Sobre ti y los tuyos' },
    { id: 'summary', label: 'Tu protección' },
    { id: 'checkout', label: 'Confirmación' },
  ];

  activeStepIndex = 0;

  private readonly destroy$ = new Subject<void>();

  selectedCategory: InsuranceCategory = InsuranceCategory.Life;

  get quoteId() {
    return this.quotesService.currentQuote?.quoteId;
  }

  constructor(private readonly router: Router) { }

  ngOnInit(): void {
    this.setActiveStepFromRoute();
    this.updateCategoryFromUrl();

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.setActiveStepFromRoute();
        this.updateCategoryFromUrl();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isStepCompleted(index: number): boolean {
    return index <= this.activeStepIndex;
  }

  private setActiveStepFromRoute(): void {
    const leafRoute = this.getLeafRoute(this.router.routerState.snapshot.root);
    const path = leafRoute?.routeConfig?.path as StepId | undefined;
    const foundIndex = path
      ? this.steps.findIndex((step) => step.id === path)
      : -1;

    this.activeStepIndex = foundIndex >= 0 ? foundIndex : 0;
  }

  private getLeafRoute(
    route: ActivatedRouteSnapshot | null,
  ): ActivatedRouteSnapshot | null {
    let currentRoute = route;

    while (currentRoute?.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    return currentRoute ?? null;
  }

  private updateCategoryFromUrl(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    const categoryParam = urlTree.queryParams['category'] as string | undefined;
    const legacyRenglon = urlTree.queryParams['renglon'] as string | undefined;

    let category: InsuranceCategory | null = null;

    if (typeof categoryParam === 'string') {
      // Expecting enum name, e.g., 'Life', 'Health', 'Accident'
      const possible = (InsuranceCategory as any)[categoryParam];
      if (typeof possible === 'string') {
        category = possible as InsuranceCategory;
      }
    }

    if (category === null && typeof legacyRenglon === 'string') {
      category = this.mapLegacyRenglonToCategory(legacyRenglon);
    }

    if (category !== null) {
      this.selectedCategory = category;
    }
  }

  private mapLegacyRenglonToCategory(
    renglon: string,
  ): InsuranceCategory | null {
    const normalized = renglon.toLowerCase();
    if (normalized.includes('vida')) return InsuranceCategory.Life;
    if (normalized.includes('salud')) return InsuranceCategory.Health;
    if (normalized.includes('accidente')) return InsuranceCategory.Accident;
    return null;
  }

  get selectedCategoryLabel(): string {
    return getCategoryLabel(this.selectedCategory);
  }
}
